import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { Button, Field, Modal } from "../../components/index.js";
import {
  buildCancelInput,
  isCancelOverrideRequired
} from "../../lib/plan-actions.js";

/**
 * Modal do botão "Cancelar" (§23). Coleta o motivo e chama
 * POST /inbound-plans/:id/cancel (ADMIN). O cancelamento é ASSÍNCRONO: a Amazon
 * (cancelInboundPlan) é chamada no handler de task; o 202 só confirma o
 * enfileiramento. Se a janela de void de 24h expirou (transporte confirmado), o
 * backend responde 409 CANCEL_REQUIRES_ADMIN_OVERRIDE — então revelamos o bloco
 * de confirmação do Admin (ciência do risco de cobrança + justificativa) e
 * reenviamos com adminOverride.
 */
export interface CancelarPlanoModalProps {
  open: boolean;
  onClose: () => void;
  plan: { id: string; name: string | null } | null;
  onCancelled: () => void;
}

export function CancelarPlanoModal({
  open,
  onClose,
  plan,
  onCancelled
}: CancelarPlanoModalProps): ReactNode {
  const { api } = useAuth();
  const [reason, setReason] = useState("");
  const [overrideRequired, setOverrideRequired] = useState(false);
  const [overrideApproved, setOverrideApproved] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideMessage, setOverrideMessage] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enfileirado, setEnfileirado] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setReason("");
    setOverrideRequired(false);
    setOverrideApproved(false);
    setOverrideReason("");
    setOverrideMessage(null);
    setErro(null);
    setEnviando(false);
    setEnfileirado(false);
  }, [open]);

  const reasonOk = reason.trim().length > 0;
  const overrideOk =
    !overrideRequired || (overrideApproved && overrideReason.trim().length > 0);
  const podeEnviar = reasonOk && overrideOk && !enviando;

  async function confirmar(): Promise<void> {
    if (plan === null || !reasonOk || !overrideOk) {
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await api.cancelPlan(
        plan.id,
        buildCancelInput(reason, overrideRequired ? overrideReason : undefined)
      );
      setEnfileirado(true);
      onCancelled();
    } catch (error: unknown) {
      if (isCancelOverrideRequired(error)) {
        // Janela de void expirada: revela o bloco de override em vez de fechar.
        setOverrideRequired(true);
        setOverrideMessage(
          error instanceof Error ? error.message : "Confirmação de Admin necessária."
        );
      } else {
        setErro(error instanceof Error ? error.message : "Falha ao cancelar o plano.");
      }
    } finally {
      setEnviando(false);
    }
  }

  if (enfileirado) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Cancelamento enfileirado"
        width={520}
        footer={<Button onClick={onClose}>Fechar</Button>}
      >
        <p style={{ fontSize: 13 }}>
          O cancelamento foi solicitado à Amazon e está sendo processado
          (assíncrono). O status do plano mudará para <strong>Cancelado</strong>{" "}
          quando a Amazon confirmar — atualize a lista em alguns instantes.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancelar plano de envio"
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={enviando}>
            Voltar
          </Button>
          <Button onClick={() => void confirmar()} disabled={!podeEnviar}>
            {enviando ? "Cancelando…" : "Cancelar plano"}
          </Button>
        </>
      }
    >
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "0.85rem" }}>
        Cancelar o plano{" "}
        <strong>{plan?.name ?? plan?.id ?? ""}</strong> na Amazon. Esta ação é
        registrada em auditoria e não pode ser desfeita.
      </p>

      <Field label="Motivo do cancelamento" required>
        <textarea
          value={reason}
          aria-label="Motivo do cancelamento"
          rows={3}
          onChange={(e) => setReason(e.target.value)}
          style={{ resize: "vertical", width: "100%" }}
        />
      </Field>

      {overrideRequired ? (
        <div
          style={{
            marginTop: "0.9rem",
            padding: "0.75rem",
            borderRadius: 9,
            border: "1px solid var(--orange)",
            background: "var(--panel-alt)"
          }}
        >
          <div style={{ fontSize: 12.5, color: "var(--orange)", fontWeight: 600, marginBottom: "0.5rem" }}>
            {overrideMessage ??
              "A janela de void de 24h expirou. É obrigatória a confirmação de um Admin."}
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: 12.5, marginBottom: "0.6rem" }}>
            <input
              type="checkbox"
              checked={overrideApproved}
              aria-label="Confirmo ciência do risco de cobrança pela Amazon"
              onChange={(e) => setOverrideApproved(e.target.checked)}
            />
            <span>Confirmo, como Admin, ciência do risco de cobrança pela Amazon (SPD Partnered Carrier).</span>
          </label>
          <Field label="Justificativa do Admin" required>
            <textarea
              value={overrideReason}
              aria-label="Justificativa do Admin"
              rows={2}
              onChange={(e) => setOverrideReason(e.target.value)}
              style={{ resize: "vertical", width: "100%" }}
            />
          </Field>
        </div>
      ) : null}

      {erro !== null ? (
        <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, marginTop: "0.85rem" }}>
          {erro}
        </p>
      ) : null}
    </Modal>
  );
}
